import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Inbox, Check, AlertCircle, Send, Trash2, Archive, Loader2, RefreshCw, Reply, Clock, User, MessageSquare, X } from 'lucide-react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

/**
 * Admin — Demandes de contact.
 *
 * Permet à l'admin de :
 *   · Lister toutes les demandes (filtre par statut)
 *   · Voir le détail
 *   · Répondre directement par email (envoi auto via Resend)
 *   · Marquer spam / archiver
 *
 * Design : panneau split (liste à gauche, détail/réponse à droite sur desktop ;
 *          stack vertical sur mobile).
 */
export default function ContactRequests() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [filter, setFilter] = useState('all'); // all | new | answered | spam | archived
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filterArg = filter === 'all' ? null : filter;
      const res = await api.adminListContactRequests(filterArg);
      setItems(res?.items || []);
      setStats({ total: res?.total || 0, new: res?.new || 0 });
    } catch (err) {
      toast.error("Impossible de charger les demandes : " + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = (item) => {
    setSelected(item);
    setReply('');
  };

  const sendReply = async () => {
    if (!selected) return;
    if (reply.trim().length < 10) {
      toast.error('Réponse trop courte (min. 10 caractères).');
      return;
    }
    setSending(true);
    try {
      await api.adminReplyContactRequest(selected.id, reply.trim());
      toast.success('Réponse envoyée par email ! La demande est marquée comme traitée.');
      setReply('');
      // Refresh list and update selection
      await load();
      setSelected(prev => prev ? { ...prev, status: 'answered', reply_message: reply.trim() } : null);
    } catch (err) {
      toast.error('Erreur d\'envoi : ' + (err?.message || ''));
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await api.adminUpdateContactRequest(selected.id, newStatus);
      toast.success(`Statut mis à jour : ${labelForStatus(newStatus)}`);
      await load();
      setSelected(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err) {
      toast.error('Erreur : ' + (err?.message || ''));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-contact-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="contact-requests-title">
            Demandes de contact
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gère les messages reçus depuis le formulaire <code className="text-[12px] bg-muted px-1.5 py-0.5 rounded">/contact</code> et réponds directement par email.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="contact-requests-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Inbox} label="Total" value={stats.total} testId="stat-total" />
        <StatCard icon={Mail} label="Nouvelles" value={stats.new} accent="primary" testId="stat-new" />
        <StatCard icon={Check} label="Traitées" value={items.filter(i => i.status === 'answered').length} accent="success" testId="stat-answered" />
        <StatCard icon={Trash2} label="Spam" value={items.filter(i => i.status === 'spam').length} accent="muted" testId="stat-spam" />
      </div>

      {/* Filtres */}
      <Tabs value={filter} onValueChange={setFilter} data-testid="contact-requests-tabs">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Toutes</TabsTrigger>
          <TabsTrigger value="new" data-testid="tab-new">Nouvelles{stats.new > 0 ? ` (${stats.new})` : ''}</TabsTrigger>
          <TabsTrigger value="answered" data-testid="tab-answered">Répondues</TabsTrigger>
          <TabsTrigger value="spam" data-testid="tab-spam">Spam</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">Archivées</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Layout split : liste + détail */}
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-4">
        {/* Liste */}
        <div className="mc-card overflow-hidden" data-testid="contact-requests-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="contact-requests-empty">
              <Inbox className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm font-semibold">Aucune demande pour l'instant</p>
              <p className="text-xs text-muted-foreground mt-1">
                {filter === 'all'
                  ? "Les demandes du formulaire /contact apparaîtront ici."
                  : `Aucune demande avec le statut "${labelForStatus(filter)}".`}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border max-h-[640px] overflow-y-auto">
              {items.map((it) => (
                <li
                  key={it.id}
                  onClick={() => handleSelect(it)}
                  className={`p-4 cursor-pointer transition-colors hover:bg-muted/40 ${selected?.id === it.id ? 'bg-muted/60 border-l-4 border-l-[hsl(var(--primary))]' : 'border-l-4 border-l-transparent'}`}
                  data-testid={`contact-request-item-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="font-bold text-sm truncate flex-1">{it.name}</div>
                    <StatusBadge status={it.status} />
                  </div>
                  <div className="text-xs text-muted-foreground truncate mb-1">{it.email}</div>
                  <div className="text-sm font-medium truncate">{it.subject}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-relaxed">{it.message}</div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-2">
                    <Clock className="w-3 h-3" />
                    {formatDate(it.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Panneau détail / réponse */}
        <div className="mc-card p-5 sm:p-6 min-h-[400px]" data-testid="contact-request-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <MessageSquare className="w-10 h-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-semibold">Sélectionne une demande</p>
              <p className="text-xs text-muted-foreground mt-1">Clique sur une carte à gauche pour voir le détail et répondre.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header demande */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-black tracking-tight mb-1" data-testid="detail-subject">{selected.subject}</h2>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /><strong className="text-foreground">{selected.name}</strong>
                    </span>
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline" data-testid="detail-email-link">
                      <Mail className="w-3.5 h-3.5" />{selected.email}
                    </a>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />{formatDate(selected.created_at)}
                    </span>
                  </div>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              {/* Message original */}
              <div className="rounded-md border border-border bg-muted/30 p-4" data-testid="detail-message">
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">Message original</div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{selected.message}</div>
              </div>

              {/* Réponse déjà envoyée ? */}
              {selected.reply_message && (
                <div className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4" data-testid="detail-reply-sent">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">Réponse envoyée</div>
                    {selected.replied_at && (
                      <div className="text-[11px] text-muted-foreground">{formatDate(selected.replied_at)}</div>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{selected.reply_message}</div>
                </div>
              )}

              {/* Formulaire réponse */}
              {selected.status !== 'spam' && selected.status !== 'archived' && (
                <div className="space-y-2" data-testid="detail-reply-form">
                  <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    <Reply className="w-3.5 h-3.5" />
                    {selected.reply_message ? 'Envoyer une nouvelle réponse' : 'Ta réponse'}
                  </label>
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={`Bonjour ${selected.name?.split(' ')[0] || 'cher motard'},\n\nMerci pour ta question…`}
                    rows={8}
                    className="resize-y min-h-[160px]"
                    disabled={sending}
                    data-testid="reply-textarea"
                  />
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-[11px] text-muted-foreground">{reply.length} / 8000</div>
                    <Button
                      onClick={sendReply}
                      disabled={sending || reply.trim().length < 10}
                      data-testid="reply-send-btn"
                    >
                      {sending ? (
                        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Envoi…</>
                      ) : (
                        <><Send className="w-4 h-4 mr-1.5" />Envoyer la réponse par email</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions de modération */}
              <div className="pt-4 border-t border-border flex flex-wrap gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground self-center mr-2">Modération :</span>
                {selected.status !== 'new' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus('new')} disabled={actionLoading} data-testid="action-mark-new">
                    <Inbox className="w-3.5 h-3.5 mr-1.5" />Remettre en nouvelle
                  </Button>
                )}
                {selected.status !== 'answered' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus('answered')} disabled={actionLoading} data-testid="action-mark-answered">
                    <Check className="w-3.5 h-3.5 mr-1.5" />Marquer traitée
                  </Button>
                )}
                {selected.status !== 'spam' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus('spam')} disabled={actionLoading} className="text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))]" data-testid="action-mark-spam">
                    <X className="w-3.5 h-3.5 mr-1.5" />Spam
                  </Button>
                )}
                {selected.status !== 'archived' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus('archived')} disabled={actionLoading} data-testid="action-mark-archived">
                    <Archive className="w-3.5 h-3.5 mr-1.5" />Archiver
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== Sub-components =====================

function StatCard({ icon: Icon, label, value, accent = 'default', testId }) {
  const accentClasses = {
    default: 'border-border',
    primary: 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5',
    success: 'border-green-500/30 bg-green-500/5',
    muted: 'border-border bg-muted/30',
  };
  const valueColor = {
    default: 'text-foreground',
    primary: 'text-[hsl(var(--primary))]',
    success: 'text-green-600 dark:text-green-400',
    muted: 'text-muted-foreground',
  };
  return (
    <div className={`mc-card p-4 border ${accentClasses[accent]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className={`text-2xl font-black ${valueColor[accent]}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    new: { label: 'Nouvelle', cls: 'bg-[hsl(var(--primary))] text-white' },
    answered: { label: 'Répondue', cls: 'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30' },
    spam: { label: 'Spam', cls: 'bg-muted text-muted-foreground border border-border' },
    archived: { label: 'Archivée', cls: 'bg-muted/60 text-muted-foreground' },
  };
  const c = cfg[status] || cfg.new;
  return (
    <span className={`inline-flex items-center text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${c.cls}`} data-testid={`status-badge-${status}`}>
      {c.label}
    </span>
  );
}

function labelForStatus(s) {
  return { new: 'Nouvelle', answered: 'Répondue', spam: 'Spam', archived: 'Archivée' }[s] || s;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
